import KpiManagementFilter from "@/components/filters/KpiManagementFilter";
import { LoadingSpinner } from "@/components/layouts/LoadingSpinner";
import NoRecord from "@/components/layouts/NoRecord";
import Search from "@/components/layouts/Search";
import TableSkeleton from "@/components/layouts/TableSkeleton";
import DataTableCustomPagination from "@/components/table/DataTableCustomPagination";
import { employeeManagementColumns } from "@/helpers/columns/employee/employee";
import { teamColumns } from "@/helpers/columns/team-management/team";
import useEmployeeFilter from "@/hooks/employee/useEmployeeFilter";
import { useErrorHandling } from "@/hooks/useErrorHandling";
import useHeaderTitle from "@/hooks/useHeaderTitle";
import usePagination from "@/hooks/usePagination";
import useSearch from "@/hooks/useSearch";
import { useGetAllEmployeeQuery } from "@/states/employee/employee.api";
import { TFailResponse } from "@/types/general/TFailResponse";
import employeeRoute from "@/utils/constants/employee/employee-route.constant";
import { castTypeArr } from "@/utils/general/cast-type";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import AddButton from "@components/buttons/CustomButton";
import { FaPlusCircle } from "react-icons/fa";
import teamRoute from "@/utils/constants/team/team-route.constant";
import {
  useDeleteTeamMutation,
  useGetAllTeamQuery,
} from "@/states/team-management/team.api";
import { Toast } from "@/helpers/popups/Toast";
import { DeletePopup } from "@/helpers/popups/DeletePopup";

const TeamPage = () => {
  useHeaderTitle("Team ");
  const navigate = useNavigate();
  const { search, setSearch } = useSearch();
  const { page, limit, handlePageChange, handlePerRowsChange } =
    usePagination();
  const {
    search: filterSearch,
    position,
    departments,
    setEmployeeFilter,
  } = useEmployeeFilter();
  const [deleteTeam, { error: deleteError }] = useDeleteTeamMutation();

  const {
    data: employeeResData,
    error: fetchEmpListError,
    isLoading,
    refetch,
  } = useGetAllTeamQuery(
    {
      pagination: { page, limit },
      searchKey: search || filterSearch,
    },
    { refetchOnMountOrArgChange: true }
  );
  const editHandler = (val: any) => {
    navigate(teamRoute.editPath(val.team_id as any), {
      state: { team: val },
    });
  };

  const deleteHandler = async (val: any) => {
    try {
      const { isConfirmed } = await DeletePopup.fire();
      if (isConfirmed) {
        // const res = await deleteTeam(val.team_id as any);
        const res = await deleteTeam({ id: val.team_id });
        if (res?.data?.success) {
          Toast.fire({
            title: res.data.message,
            icon: "success",
          });
          refetch();
        }
      }
    } catch (error) {
      Toast.fire({ icon: "error", title: "Fail to delete KRA" });
    }
  };

  useErrorHandling(
    ...castTypeArr<TFailResponse>([fetchEmpListError, deleteError])
  );

  return (
    <div className="bg-white p-8 min-h-[60vh] overflow-x-auto rounded-2xl">
      {isLoading && <LoadingSpinner />}

      <div className="w-100 flex items-center justify-end mb-4">
        <AddButton
          isMedium
          handleClick={() => navigate(teamRoute.addPath())}
          title="Add New Team"
          color="bg-blue-600"
          icon={<FaPlusCircle className="text-lg" />}
        />
      </div>
      <DataTable
        keyField="brand_id"
        responsive
        striped
        pagination
        paginationServer
        paginationTotalRows={employeeResData?.total ?? 0}
        progressPending={false}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        noDataComponent={<NoRecord />}
        progressComponent={<TableSkeleton row={10} col={4} />}
        columns={teamColumns(editHandler, deleteHandler) as any}
        data={employeeResData?.data ?? []}
        paginationComponent={DataTableCustomPagination}
        paginationRowsPerPageOptions={[5, 10, 20, 50]}
      />
    </div>
  );
};

export default TeamPage;
